import {
  IonContent,
  IonMenu,
  IonLabel,
  IonItem,
  IonMenuToggle,
  useIonAlert,
  useIonLoading,
} from "@ionic/react";

import menuRoutes from "./menuRoutes.json";
import { Icon } from "@iconify/react";
import { useHistory, useLocation } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { Capacitor } from "@capacitor/core";
import { baseUrl, getCourse, headersDefault } from "../../redux/actions";
import { useEffect, useRef, useState } from "react";
import { userAuthStatus } from "../../services/RouteAuth";
import { getAuthorizedSupportTypes } from "../../redux/actions/streamChatActions";
import { selectSupportAwaitingTotal } from "../../redux/reducer/streamChatReducer";
import ActivateNotifications from "../modals/NotificationsModal/ActivateNotifications";
import { safeNavigate } from "../../utils/navigationHelper";
import { redirectToPremium } from "../../utils/premiumRedirect";
import { useAppPermission } from "../../hooks/useAppPermission";
import usePlan from "../../hooks/usePlan";
import PermissionPopupModal from "../modals/PermissionPopupModal/PermissionPopupModal";
import {
  COMMUNITY_FEED_PATH,
  PERMISSION_ACTIONS,
} from "../../constants/permissionActions";

import "./MainMenu.css";

import { readAuth } from "../../services/authStorage";
/**
 * Header row for a collapsible menu group (e.g. "Area Team").
 *
 * The whole menu list lives inside a single <IonMenuToggle>, which closes the
 * side menu on any click via a *native* click listener on its host element.
 * React's synthetic onClick runs at the app root — i.e. AFTER Ionic's native
 * handler on an ancestor — so calling `stopPropagation()` there is too late to
 * stop the menu from closing. We instead attach a native `click` listener
 * directly on this item (a descendant of <ion-menu-toggle>) so it fires first
 * and can stop the event before it bubbles up to the toggle.
 */
const MenuGroupHeader = ({ route, isExpanded, staggerIndex, onToggle }) => {
  const itemRef = useRef(null);

  useEffect(() => {
    const el = itemRef.current;
    if (!el) return undefined;

    const handler = (e) => {
      e.stopPropagation();
      onToggle();
    };
    el.addEventListener("click", handler);
    return () => el.removeEventListener("click", handler);
  }, [onToggle]);

  return (
    <IonItem
      ref={itemRef}
      button
      detail={false}
      lines="none"
      className="menuRoute menu-item-stagger"
      style={{
        color: "#646464",
        "--min-height": "38px",
        "--inner-min-height": "38px",
        "--inner-padding-top": "2px",
        "--inner-padding-bottom": "2px",
        "--inner-padding-start": "12px",
        "--inner-padding-end": "12px",
        "--stagger-i": staggerIndex,
      }}
    >
      <Icon
        style={{
          width: "19px",
          height: "19px",
          marginInlineEnd: "8px",
        }}
        icon={route.icon}
        slot="start"
      />
      <IonLabel style={{ fontSize: "1.12rem" }}>{route.title}</IonLabel>
      <Icon
        icon={isExpanded ? "ri:arrow-up-s-line" : "ri:arrow-down-s-line"}
        slot="end"
        style={{ width: "20px", height: "20px" }}
      />
    </IonItem>
  );
};

const MainMenu = () => {
  const history = useHistory();
  const location = useLocation();
  const pathname = location.pathname;

  const {
    runIfAllowed: runIfViewFeedAllowed,
    isModalOpen: isViewFeedModalOpen,
    setIsModalOpen: setIsViewFeedModalOpen,
    popupText: viewFeedPopupText,
  } = useAppPermission(PERMISSION_ACTIONS.FEED_COMMUNITY_VIEW_FEED);

  const [userStatus, setUserStatus] = useState(null);

  const [showNotificationsModal, setShowNotificationsModal] = useState(false);

  // Bumped each time the menu starts opening — used as a `key` on the
  // items wrapper so React remounts the list, which re-fires the CSS
  // stagger animation on every open (not just on initial mount).
  const [openCount, setOpenCount] = useState(0);

  // Tracks which collapsible menu groups (e.g. "Area Team") are expanded.
  const [expandedGroups, setExpandedGroups] = useState({});

  const toggleGroup = (title) =>
    setExpandedGroups((prev) => ({ ...prev, [title]: !prev[title] }));

  useEffect(() => {
    setUserStatus(userAuthStatus());

    const permission =
      "Notification" in window ? Notification?.permission : "default";
    // console.log('permission', permission)
    if (permission === "default") {
      const subscriptionType = JSON.parse(
        readAuth("adoc_p") ?? "{}",
      )?.master_club_subscriber?.subscription?.subscription_type?.name;

      const tries = subscriptionType === "premium" ? 51 : 21;
      let adoc_nav_count = localStorage.getItem("adoc_nav_count") ?? tries;

      adoc_nav_count--;
      localStorage.setItem("adoc_nav_count", adoc_nav_count);
      if (adoc_nav_count < 1) {
        localStorage.setItem("adoc_nav_count", tries);
        setShowNotificationsModal(true);
      }
    }
  }, [pathname]);

  const dispatch = useDispatch();
  const [presentAlert] = useIonAlert();
  const [presentLoading, dismissLoading] = useIonLoading();

  const subscriber =
    useSelector((state) => state.user?.master_club_subscriber) ?? null;

  // Team/staff flag. Menu items marked `requiresBadge` (the "Area Team"
  // group and everything inside it) are only shown to users that carry a
  // backend-assigned badge.
  const hasBadge = !!subscriber?.badge;

  // Tutor badge holders see Area Team → Percorso FREE even without another
  // team badge; every other Area Team entry still needs one.
  const isFreeTutor = useSelector((state) => state.user?.free_program_tutor === true);

  // Conversations waiting for a reply, shown on the "Chat supporto" entry so
  // staff can see outstanding work without opening the console.
  const supportAwaitingTotal = useSelector(selectSupportAwaitingTotal);
  useEffect(() => {
    if (!hasBadge) return;
    dispatch(getAuthorizedSupportTypes());
  }, [dispatch, hasBadge]);

  const subscription = subscriber?.subscription;

  const subscriptionName = subscription?.subscription_type?.name;

  const menuUserType =
    subscriptionName === "premium"
      ? "premium"
      : subscriptionName === "free"
      ? "free"
      : "guest";
  // Premium Lite is type "premium" too; entries can still name it apart
  // ("Passa a Premium" stays for Lite, Phase 4).
  const { isPremiumLite } = usePlan();

  useEffect(() => {
    if (userStatus === "full" && pathname !== "/corso") {
      // console.log('getCourse')
      dispatch(getCourse());
    }
  }, [pathname, userStatus]);

  const handleLogoutConfirm = () => {
    presentAlert({
      header: "Sei sicuro di voler uscire dall'app?",
      buttons: [
        { text: "Cancella", role: "cancel" },
        {
          text: "Si, confermo",
          role: "confirm",
          handler: () => safeNavigate(history, "/logout", pathname),
        },
      ],
    });
  };

  const navigateWithViewFeedPermission = (targetUrl) => {
    const path = String(targetUrl || "")
      .split("?")[0]
      .split("#")[0];

    if (path === COMMUNITY_FEED_PATH) {
      runIfViewFeedAllowed(() => safeNavigate(history, targetUrl, pathname));
      return;
    }

    safeNavigate(history, targetUrl, pathname);
  };

  const handlePremiumRedirect = async () => {
    // On native, redirectToPremium synchronously navigates to /paywall — no
    // loading overlay needed and showing one races with the dismiss because
    // navigation completes before Ionic's loading mounts.
    if (Capacitor.getPlatform() !== "web") {
      await redirectToPremium({ history, pathname });
      return;
    }

    // On web, redirectToPremium does an HTTP fetch to resolve the active
    // premium widget, so a loading overlay during that round-trip is real UX.
    // Await both calls so present/dismiss can't race.
    await presentLoading({ message: "Caricando..." });
    try {
      await redirectToPremium({ history, pathname });
    } finally {
      await dismissLoading();
    }
  };

  return (
    <IonMenu
      id="main-menu"
      contentId="main-content"
      // Push the main content to the right as the menu slides in,
      // matching the reference animation.
      type="push"
      // Ionic's built-in side-menu gesture covers both directions:
      // swipe LEFT on an open menu to close it (returning the user
      // to the Dashboard with the push animation playing in reverse)
      // AND swipe RIGHT from the left edge to open it again. The
      // close half is what was asked for; the open half is the
      // standard mobile pattern that comes along for free.
      swipeGesture={true}
      onIonWillOpen={() => setOpenCount((c) => c + 1)}
      style={{
        "--min-width": "260px",
        "--width": "260px",
        "--max-width": "260px",
      }}
    >
      {/*
        The scroll area needs room for the Android navigation bar. Without the
        bottom inset the last rows (Profilo & Contatti, Logout) sit underneath
        it: on a tall screen there is nothing to scroll to reach them, and on a
        short one the list ends flush against the bar. The top already uses
        --ion-safe-area-top the same way.
      */}
      <IonContent
        style={{
          "--padding-bottom": "calc(var(--ion-safe-area-bottom, 0px) + 1.5rem)",
        }}
      >
        {/* Per-user-type tagline. The "Ciao <name>" and "(Utente XXXX)"
            lines were removed; what stays is the contextual line that
            varies by subscription (currently shown for premium only). */}
        <div
          className="menu-content"
          style={{
            padding: "0 1rem",
            color: "#222",
            paddingTop: "calc(var(--ion-safe-area-top, 0px) + 1.5rem)",
          }}
        >
          {menuUserType === "premium" ? (
            <div
              style={{
                fontWeight: 700,
                fontSize: "1.15rem",
                marginBottom: "0.75rem",
              }}
            >
              Buon percorso!
            </div>
          ) : null}
        </div>
        <IonMenuToggle>
          {/* `key={openCount}` forces React to remount the items on every
              menu open, which re-triggers the CSS stagger animation. The
              `main-menu-items` wrapper class is what scopes the keyframe. */}
          <div key={openCount} className="main-menu-items">
            {(() => {
              // Visible-position counter — separate from the source-array
              // index so filtered-out routes (divider / visibleFor /
              // subscriptions) don't create gaps in the stagger timing.
              let visibleIndex = 0;
              return menuRoutes.map((route, index) => {
                if (
                  route.visibleFor &&
                  !route.visibleFor.includes(menuUserType) &&
                  !(isPremiumLite && route.visibleFor.includes("premium_lite"))
                ) {
                  return null;
                }

                // Badge-gated items (e.g. the "Area Team" group) are only
                // visible to team members that carry a backend badge.
                if (route.requiresBadge && !hasBadge && !isFreeTutor) return null;

                if (route.type === "divider") return null;

                // Collapsible group (header + nested navigation children).
                if (route.type === "group") {
                  const isExpanded = !!expandedGroups[route.title];
                  const headerIndex = visibleIndex++;
                  return (
                    <div key={route.title}>
                      <MenuGroupHeader
                        route={route}
                        isExpanded={isExpanded}
                        staggerIndex={headerIndex}
                        onToggle={() => toggleGroup(route.title)}
                      />

                      {isExpanded &&
                        (route.children || [])
                          .filter((child) => (child.requiresFreeTutor ? isFreeTutor : !route.requiresBadge || hasBadge))
                          .map((child) => {
                          const childPathname = (child.url || "")
                            .split("?")[0]
                            .split("#")[0];
                          const childSelected =
                            !!childPathname &&
                            (pathname === childPathname ||
                              pathname.startsWith(childPathname + "/"));
                          const childIndex = visibleIndex++;
                          return (
                            <IonItem
                              key={child.title}
                              button
                              onClick={() =>
                                navigateWithViewFeedPermission(child.url)
                              }
                              lines="none"
                              className={
                                (childSelected
                                  ? "menuRouteSelected"
                                  : "menuRoute") + " menu-item-stagger"
                              }
                              style={{
                                color: "#646464",
                                "--min-height": "34px",
                                "--inner-min-height": "34px",
                                "--inner-padding-top": "2px",
                                "--inner-padding-bottom": "2px",
                                "--inner-padding-start": "34px",
                                "--inner-padding-end": "12px",
                                "--stagger-i": childIndex,
                              }}
                            >
                              <Icon
                                style={{
                                  width: "17px",
                                  height: "17px",
                                  marginInlineEnd: "8px",
                                }}
                                icon={child.icon}
                                slot="start"
                              />
                              <IonLabel style={{ fontSize: "1.02rem" }}>
                                {child.title}
                              </IonLabel>
                              {child.url === "/area-team/chat-supporto" &&
                                supportAwaitingTotal > 0 && (
                                  <span
                                    slot="end"
                                    className="menu-support-awaiting"
                                    title="Conversazioni in attesa di risposta"
                                  >
                                    {supportAwaitingTotal}
                                  </span>
                                )}
                            </IonItem>
                          );
                        })}
                    </div>
                  );
                }

                // Handle button type items with conditional logic
                if (route.type === "button") {
                  const handleButtonClick = async () => {
                    if (route.url === "/logout") {
                      handleLogoutConfirm();
                      return;
                    }
                    if (route.title === "Passa a Premium") {
                      await handlePremiumRedirect();
                      return;
                    }
                    if (route.url) {
                      navigateWithViewFeedPermission(route.url);
                    }
                  };
                  // Course CTAs ("Il tuo Percorso" → /corso for free users,
                  // "Inizia Gratis" → /percorso-gratuito for guests). Keyed on
                  // url so the relabeled item keeps its highlighted styling.
                  const isCourseCta =
                    route.url === "/corso" ||
                    route.url === "/percorso-gratuito";
                  const isUpgrade = route.title === "Passa a Premium";
                  const isLogout = route.title === "Logout";

                  const myIndex = visibleIndex++;
                  return (
                    <IonItem
                      key={route.title}
                      button
                      onClick={handleButtonClick}
                      lines={
                        route.hideLines ||
                        (isUpgrade
                          ? "none"
                          : isCourseCta
                          ? ""
                          : route.url === pathname
                          ? "none"
                          : "")
                      }
                      className="menuRoute menu-item-stagger"
                      style={{
                        color: "#646464",
                        "--min-height": "34px",
                        "--inner-min-height": "34px",
                        "--inner-padding-top": "2px",
                        "--inner-padding-bottom": "2px",
                        "--inner-padding-start": isCourseCta ? "8px" : "12px",
                        "--inner-padding-end": "12px",
                        "--stagger-i": myIndex,
                        marginBottom: isUpgrade ? "16px" : "0",
                        marginTop: isLogout ? "1.5rem" : "0",
                      }}
                    >
                      <Icon
                        style={{
                          width: isCourseCta ? "20px" : "17px",
                          height: isCourseCta ? "20px" : "17px",
                          color: isCourseCta ? "#f4c542" : undefined,
                          marginInlineEnd: "8px",
                        }}
                        icon={route.icon}
                        slot="start"
                      />
                      <IonLabel
                        style={{
                          fontSize: isCourseCta ? "1.2rem" : "1.05rem",
                          fontWeight: isCourseCta ? 800 : 600,
                        }}
                      >
                        {route.title}
                      </IonLabel>
                    </IonItem>
                  );
                }

                // Handle regular menu items
                if (
                  route.subscriptions &&
                  !route.subscriptions.includes(subscriptionName)
                )
                  return null;

                const rawUrl = route.url || "";

                // "#/home" style routes are treated as external/alias => never mark active
                const isHashRoute = rawUrl.startsWith("#/");

                const routePathname = isHashRoute
                  ? ""
                  : rawUrl.split("?")[0].split("#")[0];

                const isSelected =
                  !!routePathname &&
                  (pathname === routePathname ||
                    pathname.startsWith(routePathname + "/"));

                const myIndex = visibleIndex++;
                return (
                  <IonItem
                    key={route.title}
                    button
                    onClick={() => {
                      if (route.url === "/logout") {
                        handleLogoutConfirm();
                        return;
                      }
                      let targetUrl =
                        route.url + (route.cache ? "#" + Date.now() : "");

                      // Normalize "#/home" style routes
                      if (targetUrl.startsWith("#/"))
                        targetUrl = targetUrl.slice(1);

                      navigateWithViewFeedPermission(targetUrl);
                    }}
                    lines={
                      route.hideLines || route.url === pathname ? "none" : ""
                    }
                    className={
                      (route.className || "") +
                      " " +
                      (isSelected ? "menuRouteSelected" : "menuRoute") +
                      " menu-item-stagger"
                    }
                    style={{
                      color: "#646464",
                      "--min-height": "38px",
                      "--inner-min-height": "38px",
                      "--inner-padding-top": "2px",
                      "--inner-padding-bottom": "2px",
                      "--inner-padding-start": "12px",
                      "--inner-padding-end": "12px",
                      "--stagger-i": myIndex,
                      marginTop: route.url === "/logout" ? "1rem" : "0",
                    }}
                  >
                    <Icon
                      style={{
                        width: "19px",
                        height: "19px",
                        marginInlineEnd: "8px",
                      }}
                      icon={route.icon}
                      slot="start"
                    />
                    <IonLabel style={{ fontSize: "1.12rem" }}>
                      {route.title}
                    </IonLabel>
                  </IonItem>
                );
              });
            })()}
          </div>
        </IonMenuToggle>
        <ActivateNotifications
          redirectHome={false}
          showNotificationsModal={showNotificationsModal}
          setShowNotificationsModal={setShowNotificationsModal}
        />
        <PermissionPopupModal
          isOpen={isViewFeedModalOpen}
          setIsOpen={setIsViewFeedModalOpen}
          message={viewFeedPopupText}
        />
      </IonContent>
    </IonMenu>
  );
};

export default MainMenu;
